/**
 * Session principal — the BROWSER-SAFE half of the internal-lane session auth.
 *
 * This module holds only what a contract module may reference: the principal
 * type and Tag, the wire error schemas, and the middleware Tag itself. It
 * imports nothing server-only. `session-auth-middleware.ts` carries the
 * RESOLUTION (`resolveSessionPrincipal`) and the Live layers, and imports
 * `AuthorisationService` → `clerk-auth-helper` → `next/headers` — a module the
 * client bundle must never reach. Because an `HttpApiGroup` binds its
 * middleware Tag in the contract file the browser imports, Tag and Live layer
 * can never share a module on a lane whose contract is browser-imported.
 *
 * See `session-auth-middleware.ts` for the resolution semantics and the
 * header/security commentary.
 */
import { Context, Effect, Schema as S } from "effect"
import { HttpApiMiddleware, HttpApiSchema } from "@effect/platform"
import type { CovaUserId, OrgId } from "@/domain/shared/branded-types"

// ============================================================================
// Principal
// ============================================================================

/**
 * The resolved caller. `covaUserId` is nullable because `org_members.user_id`
 * is nullable for rows the v4.7 identity backfill has not reconciled — see
 * `requireCovaUserId` for the fail-closed guard every `users(id)` FK writer
 * must use.
 */
export interface SessionPrincipal {
  readonly clerkUserId: string
  readonly covaUserId: CovaUserId | null
  readonly organizationId: OrgId
  readonly role: string
  readonly region: string
  /**
   * The firm's own IANA zone (`organisations.time_zone`), or null when the firm
   * has not set one. Optional: only the session lane reads it. Readers resolve
   * the day through `firmTimeZone(timeZone, region)`, which falls back to the
   * region profile's default zone and then UTC.
   */
  readonly timeZone?: string | null
  readonly isAdmin: boolean
  /**
   * The platform scope this request runs in (spec customer-success/v1.5.1/001
   * §2.3, README S3): `insurance-broker` for a broker firm, `staff` for the staff firm.
   * The acting firm's scope (022 G48b), admitted by `resolveSessionPrincipal`;
   * never from a header or a payload. OPTIONAL for the same
   * reason as `capabilities`; readers use `scopeOf(principal)` from
   * `@/domain/scope`, which defaults to `insurance-broker`.
   */
  readonly scope?: string
  /**
   * The principal's live staff capability grants (spec 001 E4), carried so the
   * read audit can snapshot what the actor HELD at the moment of the read
   * (011 §2 `actor_capabilities`). A grant revoked next week must not rewrite
   * last week's trail, which is why the snapshot is stored rather than joined.
   *
   * OPTIONAL, not defaulted: the dozens of literals that construct a principal
   * in tests and in the other session middlewares keep compiling, and every
   * reader takes `?? []`. `S.String`-shaped rather than the branded key for the
   * reason the identity schema gives — this module must not import the app's
   * capability registry.
   */
  readonly capabilities?: ReadonlyArray<string>
  /**
   * The member's OWN org admin flag (`org_members.is_org_admin`), whatever the
   * capability set says. `isAdmin` above is the collapsed "org admin or super
   * admin", and the cutover's agreement write must be the firm's admin, not a
   * staffer who happens to be a member too (research 01 §2.3.5).
   *
   * OPTIONAL for the reason `capabilities` gives: the literals that build a
   * principal keep compiling. `resolveSessionPrincipal` always sets it. A
   * reader that needs a yes treats absent as no.
   */
  readonly isOrgAdmin?: boolean
  /**
   * Where the firm's clients live (cutover ruling 7): `book` once its move is
   * done, `legacy` otherwise. Routing only. Optional for the same reason;
   * always set by `resolveSessionPrincipal`.
   */
  readonly bookMode?: "legacy" | "book"
  /**
   * Whether the legacy clients tables take the firm's writes: false from
   * `migrating` onward (cutover ruling 16). Optional for the same reason;
   * always set by `resolveSessionPrincipal`.
   */
  readonly legacyWritable?: boolean
  /**
   * The keys of the firm's switches whose row is on (decision 033). The BMS
   * session middleware admits a request only when its route's switches are on
   * (`switchesForRoute`), so absent reads as none on, never as all. Optional
   * for the reason `capabilities` gives; always set by `resolveSessionPrincipal`.
   */
  readonly firmSwitches?: ReadonlyArray<string>
  /**
   * Whether the firm is waiting to come onto Cova CRM (decision 036, the
   * waiting gate): the BMS session middleware then admits only the firm's
   * admins, and only on the routes Settings' connect, progress and cutover
   * pages and the firm's configuration need (`waitingAdmitsRoute`). Optional
   * for the reason `capabilities` gives, and absent reads as not waiting,
   * because the resolved identity carries it as required and
   * `resolveSessionPrincipal` always copies it.
   */
  readonly firmWaiting?: boolean
}

export class SessionPrincipalTag extends Context.Tag("SessionPrincipal")<
  SessionPrincipalTag,
  SessionPrincipal
>() {}

/**
 * `region` is a required string on the principal but nullable on the identity
 * (an org with no region configured). Region-aware behaviour must not silently
 * default to AU, so the unset state is carried as this explicit sentinel rather
 * than a plausible-looking region.
 *
 * Consumers MUST read it as "this org has no region configured" and branch on
 * the exported constant — never on a bare `"unset"` literal at a use site.
 *
 * SECURITY: never let this value participate in an authorisation or routing
 * decision. It is not a region, so a comparison that treats it as one (a
 * region allowlist, a residency check, a corpus selector) would be matching on
 * the absence of configuration. Fail closed on it explicitly instead.
 */
export const SESSION_REGION_UNSET = "unset"

// ============================================================================
// Errors
// ============================================================================

export class SessionAuthError extends S.TaggedError<SessionAuthError>()(
  "SessionAuthError",
  { message: S.String },
  HttpApiSchema.annotations({ status: 401 }),
) {}

export class SessionAuthUnavailableError extends S.TaggedError<SessionAuthUnavailableError>()(
  "SessionAuthUnavailableError",
  { message: S.String },
  HttpApiSchema.annotations({ status: 503 }),
) {}

/**
 * The caller is authenticated but has no canonical `users.id`, so any write
 * that FKs to `users(id)` cannot be attributed. Fail CLOSED (403) rather than
 * writing a NULL actor — spec 002 §2.1.
 */
export class SessionIdentityIncompleteError extends S.TaggedError<SessionIdentityIncompleteError>()(
  "SessionIdentityIncompleteError",
  { message: S.String },
  HttpApiSchema.annotations({ status: 403 }),
) {}

/**
 * The request's scope is one this caller may not enter (spec
 * customer-success/v1.5.2/005 T1).
 *
 * 403, NOT 401 — the distinction is the whole point of the class. Before it,
 * an admission denial was dressed as `SessionAuthError` (401) and both
 * enveloped lanes re-rendered that as "Sign in to continue.", so a staffer
 * whose `/cs` request was refused was shown a logout for a problem signing in
 * again could never fix. The session is valid; the PRODUCT is not theirs.
 *
 * `code` is the machine-readable half (`PLATFORM_SCOPE_INVALID` |
 * `PLATFORM_SCOPE_NOT_ADMITTED`, from `platform-scope-codes.ts`) that the old
 * mapping discarded. `S.String` rather than the literal union because this
 * browser-safe module must not import the wire-code family; the middleware
 * passes a `PlatformScopeWireCode` and a unit test pins the two values.
 *
 * The MESSAGE is the admission helper's sanitised copy. It never names the
 * missing capability and never echoes the rejected header value — that value
 * is attacker-controlled free text.
 */
export class SessionScopeDeniedError extends S.TaggedError<SessionScopeDeniedError>()(
  "SessionScopeDeniedError",
  { message: S.String, code: S.String },
  HttpApiSchema.annotations({ status: 403 }),
) {}

// ============================================================================
// Middleware
// ============================================================================

export class SessionAuthMiddleware extends HttpApiMiddleware.Tag<SessionAuthMiddleware>()(
  "SessionAuthMiddleware",
  {
    provides: SessionPrincipalTag,
    failure: S.Union(SessionAuthError, SessionAuthUnavailableError, SessionScopeDeniedError),
  },
) {}

/**
 * `AuthorisationService.getIdentity()` maps a REPOSITORY failure onto the same
 * `AuthorisationError` type it uses for "not a member"
 * (`authorisation-service.ts:97-101`), so the tag alone cannot tell a database
 * outage from a legitimately unauthenticated caller. Discriminating on this
 * message keeps an incident from logging every internal user out with a 401 —
 * the same string-equality discrimination `ACTIVE_MEMBER_NOT_FOUND_MESSAGE`
 * already relies on. Pinned by `session-auth-middleware.test.ts`; if the
 * upstream message changes, that test fails rather than the split silently
 * degrading to 401.
 */
export const IDENTITY_RESOLUTION_FAILURE_MESSAGE =
  "Failed to get user role information"

// ============================================================================
// Guards
// ============================================================================

/**
 * Fail-closed accessor for operations writing a `users(id)` FK. Call it at the
 * top of the handler; never write `principal.covaUserId!`.
 */
export const requireCovaUserId = (
  principal: SessionPrincipal,
): Effect.Effect<CovaUserId, SessionIdentityIncompleteError> =>
  principal.covaUserId === null
    ? Effect.fail(
        new SessionIdentityIncompleteError({
          message:
            "Your Cova identity is not fully provisioned. Contact support.",
        }),
      )
    : Effect.succeed(principal.covaUserId)
